import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster, toast } from "sonner";
import { useLang } from "./LangContext";

const subjectOptions = [
  { value: "", label: "Choisir un service..." },
  { value: "Photo immobilière", label: "Photo immobilière" },
  { value: "Vidéo immobilière", label: "Vidéo immobilière" },
  { value: "Pack complet (Photo + Vidéo)", label: "Pack complet (Photo + Vidéo)" },
  { value: "Réseaux sociaux", label: "Réseaux sociaux" },
  { value: "Autre", label: "Autre" },
];

export default function Contact() {
  const { t } = useLang();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.form.errorRequired);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "e905a8e6-4c89-4523-b831-08c6007f70cb",
          from_name: "Prodimmo Website",
          subject: form.subject || "Nouveau message - Prodimmo",
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error(t.contact.form.error);
      }
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-white">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">{t.contact.label}</span>
          <h2 data-testid="contact-title" className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            {t.contact.title}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Florent Zogaj</h3>
              <p className="text-slate-500 text-sm">{t.contact.person}</p>
            </div>
            <div className="space-y-5">
              <a href="mailto:contact@prodimmo.ch" data-testid="contact-email-link" className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-[#73B5E2]/10 flex items-center justify-center group-hover:bg-[#73B5E2]/20 transition-colors"><Mail className="w-5 h-5 text-[#73B5E2]" /></div>
                <div><p className="text-xs text-slate-400 uppercase tracking-wider">{t.contact.email}</p><p className="text-slate-700 font-medium">contact@prodimmo.ch</p></div>
              </a>
              <a href="tel:+41794595160" data-testid="contact-phone-link" className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-[#73B5E2]/10 flex items-center justify-center group-hover:bg-[#73B5E2]/20 transition-colors"><Phone className="w-5 h-5 text-[#73B5E2]" /></div>
                <div><p className="text-xs text-slate-400 uppercase tracking-wider">{t.contact.phone}</p><p className="text-slate-700 font-medium">+41 79 459 51 60</p></div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#73B5E2]/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-[#73B5E2]" /></div>
                <div><p className="text-xs text-slate-400 uppercase tracking-wider">{t.contact.location}</p><p className="text-slate-700 font-medium">{t.contact.locationValue}</p></div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.3 }} className="lg:col-span-3">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="bg-[#73B5E2]/5 border border-[#73B5E2]/20 rounded-2xl p-10 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-[#73B5E2]/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-[#73B5E2]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Merci pour votre message !
                </h3>
                <p className="text-slate-600 mb-6">
                  Nous avons bien reçu votre demande et nous vous répondrons sous 24 heures.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="text-[#73B5E2] hover:text-[#5AA0D0] text-sm font-medium transition-colors"
                >
                  Envoyer un autre message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 text-sm mb-1.5 block">{t.contact.form.name} *</Label>
                    <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder={t.contact.form.namePh} data-testid="contact-input-name" className="rounded-xl border-slate-200 focus:border-[#73B5E2] focus:ring-[#73B5E2]/20" required />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700 text-sm mb-1.5 block">{t.contact.form.email} *</Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder={t.contact.form.emailPh} data-testid="contact-input-email" className="rounded-xl border-slate-200 focus:border-[#73B5E2] focus:ring-[#73B5E2]/20" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 text-sm mb-1.5 block">{t.contact.form.phone}</Label>
                    <Input id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder={t.contact.form.phonePh} data-testid="contact-input-phone" className="rounded-xl border-slate-200 focus:border-[#73B5E2] focus:ring-[#73B5E2]/20" />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-slate-700 text-sm mb-1.5 block">{t.contact.form.subject}</Label>
                    <select
                      id="subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      data-testid="contact-select-subject"
                      className="flex h-10 w-full rounded-xl border border-slate-200 bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#73B5E2]/20 focus-visible:border-[#73B5E2]"
                    >
                      {subjectOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-slate-700 text-sm mb-1.5 block">{t.contact.form.message} *</Label>
                  <textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder={t.contact.form.messagePh} rows={5} data-testid="contact-input-message" className="flex w-full rounded-xl border border-slate-200 bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#73B5E2]/20 focus-visible:border-[#73B5E2] disabled:cursor-not-allowed disabled:opacity-50 resize-none" required />
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <button type="submit" data-testid="contact-submit-button" disabled={loading} className="bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-8 py-3.5 text-sm font-medium transition-all hover:scale-105 shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2">
                    {loading ? (
                      <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t.contact.form.sending}</span>
                    ) : (
                      <span className="flex items-center gap-2"><Send className="w-4 h-4" /> {t.contact.form.submit}</span>
                    )}
                  </button>
                  <span className="flex items-center gap-1.5 text-xs text-slate-400">
                    <Clock className="w-3.5 h-3.5" />
                    Réponse sous 24h
                  </span>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
